import { generateSources } from '@/app/components/media/image/generate-sources';
import type { VideoSource } from '@/app/components/media/video/types';
import { type PopupContentItem } from '@/app/lcv/components/sections/spartan/engine/sections/hero-animation/engine-story/hotspots/popup/content/types';
import { COMPARE_30_REMARK } from '@/app/lcv/contents/spartan/engine/shared/22biturbo-specifications.data';

const ENGINE_INTRO_VIDEO = {
  src: {
    desktop: 'https://assets.isuzu-tis.com/hero_1_desktop_34411495bb.mp4',
    mobile: 'https://assets.isuzu-tis.com/hero_1_mobile_6f8a1fe760.mp4',
  },
  cover: {
    desktop: 'https://assets.isuzu-tis.com/engine_seq_1_desktop_50621bbe92.jpg',
    mobile: 'https://assets.isuzu-tis.com/engine_seq_1_mobile_44427eda72.jpg',
  },
} as const;

export const ENGINE_INTRO_SECTION_DATA = {
  videoSources: ENGINE_INTRO_VIDEO,
  title: 'พลังใหม่! ไบเทอร์โบ <span>แรง...หยุดโลก</span>',
  description:
    '<span>แรงกว่า!</span> <span>ออกตัวดีกว่า</span> <span>เร่งแซงดีกว่า</span><br />ให้พลังฉุดลากเหนือกว่า',
};

// ----------------------------------------------------------------------------

export const ENGINE_SPEED_VIDEO = {
  src: {
    desktop: 'https://assets.isuzu-tis.com/hero_2_desktop_27f22746e4.mp4',
    mobile: 'https://assets.isuzu-tis.com/hero_2_mobile_35003d6721.mp4',
  },
  cover: {
    desktop: 'https://assets.isuzu-tis.com/engine_seq_2_desktop_dd03ae1fbb.jpg',
    mobile: 'https://assets.isuzu-tis.com/engine_run_2_mobile_b50f0deea6.jpg',
  },
  endLoop: {
    src: {
      desktop:
        'https://assets.isuzu-tis.com/hero_2_loop_desktop_49bbfe6b1c.mp4',
      mobile: 'https://assets.isuzu-tis.com/hero_2_loop_mobile_880881a270.mp4',
    },
    cover: {
      desktop:
        'https://assets.isuzu-tis.com/running_loop_mobile_2d6f5b1646.jpg',
      mobile: 'https://assets.isuzu-tis.com/running_loop_mobile_2d6f5b1646.jpg',
    },
  },
} as const;

const SPEED_VIDEO_SOURCES = {
  img: {
    src: ENGINE_SPEED_VIDEO.cover.mobile,
    alt: '',
    width: 1920,
    height: 1080,
  },
  responsives: generateSources('overall', {
    mobileSrc: ENGINE_SPEED_VIDEO.cover.mobile,
    desktopSrc: ENGINE_SPEED_VIDEO.cover.desktop,
  }),
  video: {
    mobile: ENGINE_SPEED_VIDEO.src.mobile,
    desktop: ENGINE_SPEED_VIDEO.src.desktop,
  },
} satisfies VideoSource;

const SPEED_END_LOOP_VIDEO_SOURCES = {
  img: {
    src: ENGINE_SPEED_VIDEO.endLoop.cover.mobile,
    alt: '',
    width: 1920,
    height: 1080,
  },
  responsives: generateSources('overall', {
    mobileSrc: ENGINE_SPEED_VIDEO.endLoop.cover.mobile,
    desktopSrc: ENGINE_SPEED_VIDEO.endLoop.cover.desktop,
  }),
  video: {
    mobile: ENGINE_SPEED_VIDEO.endLoop.src.mobile,
    desktop: ENGINE_SPEED_VIDEO.endLoop.src.desktop,
  },
} satisfies VideoSource;

export const ENGINE_SPEED_SECTION_DATA = {
  title: '2.2 Ddi <span>MAXFORCE</span> <span>Biturbo</span>',
  preTitle: 'ครั้งแรกในโลก! กับเครื่องยนต์',
  videoSources: SPEED_VIDEO_SOURCES,
  endLoopVideoSources: SPEED_END_LOOP_VIDEO_SOURCES,
  remark: COMPARE_30_REMARK,
};

// ----------------------------------------------------------------------------

export const ENGINE_STORY_VIDEO = {
  src: {
    desktop: 'https://assets.isuzu-tis.com/hero_3_desktop_e06b2c4b8e.mp4',
    mobile: 'https://assets.isuzu-tis.com/hero_3_mobile_795836bd2b.mp4',
  },
  cover: {
    desktop: 'https://assets.isuzu-tis.com/scene_3_desktop_80d844d434.jpg',
    mobile: 'https://assets.isuzu-tis.com/scene_3_mobile_14a7205c79.jpg',
  },
  introConnectedScene: {
    src: {
      desktop:
        'https://assets.isuzu-tis.com/hero_3_intro_desktop_55375d4e77.mp4',
      mobile: 'https://assets.isuzu-tis.com/hero_3_intro_mobile_86e69c44ce.mp4',
    },
    cover: {
      desktop:
        'https://assets.isuzu-tis.com/scene_2to3_out_loop_desktop_915c227082.jpg',
      mobile:
        'https://assets.isuzu-tis.com/scene_2to3_out_loop_mobile_114b58a7e8.jpg',
    },
  },
} as const;

const STORY_VIDEO_SOURCES = {
  img: {
    src: ENGINE_STORY_VIDEO.cover.mobile,
    alt: '',
    width: 1920,
    height: 1080,
  },
  responsives: generateSources('overall', {
    mobileSrc: ENGINE_STORY_VIDEO.cover.mobile,
    desktopSrc: ENGINE_STORY_VIDEO.cover.desktop,
  }),
  video: {
    mobile: ENGINE_STORY_VIDEO.src.mobile,
    desktop: ENGINE_STORY_VIDEO.src.desktop,
  },
} satisfies VideoSource;

const STORY_CONNECTED_SCENE_VIDEO_SOURCES = {
  img: {
    src: ENGINE_STORY_VIDEO.introConnectedScene.cover.mobile,
    alt: '',
    width: 1920,
    height: 1080,
  },
  responsives: generateSources('overall', {
    mobileSrc: ENGINE_STORY_VIDEO.introConnectedScene.cover.mobile,
    desktopSrc: ENGINE_STORY_VIDEO.introConnectedScene.cover.desktop,
  }),
  video: {
    mobile: ENGINE_STORY_VIDEO.introConnectedScene.src.mobile,
    desktop: ENGINE_STORY_VIDEO.introConnectedScene.src.desktop,
  },
} satisfies VideoSource;

// Order by bottom to top from hotspot view
export const HOTSPOT_ITEMS: PopupContentItem[] = [
  {
    hotspotId: 'biturbo',
    title:
      'Biturbo <br class="hidden md:block"><span>ระบบอัดอากาศ</span><span>แบบเทอร์โบคู่</span>',
    hotspotTitle:
      'Biturbo <span>ระบบอัดอากาศ</span><br><span>แบบเทอร์โบคู่</span>',
    description:
      'ผสานการทํางานของเทอร์โบ แรงดันต่ำแปรผัน (VGS Low-pressure turbo) และ<span>เทอร์โบ</span><span>แรงดันสูง</span> (High-pressure turbo) <span>ตอบสนองไวตั้งแต่รอบต่ำ</span> <span>ออกตัวดี</span> <span>เร่งแซงมั่นใจ</span>',
    videoSources: {
      image: {
        src: 'https://assets.isuzu-tis.com/bi_turbo_93d4204d72.jpg',
      },
      video: {
        src: 'https://assets.isuzu-tis.com/isuzu_22_ddi_maxforce_biturbo_64ae4f3ad2.mp4',
      },
    },
  },
  {
    hotspotId: 'transmission',
    title: 'เกียร์ 8 สปีด',
    isNew: false,
    description:
      'ปรับจูนใหม่! เกียร์ออโตเมติก 8 สปีด REV TRONIC <span>ถ่ายทอดพละกำลังต่อเนื่อง</span> <span>แม่นยำ</span> รองรับพละกำลังที่สูงขึ้นพร้อม <span>เกียร์ธรรมดา 6 สปีดให้เลือก</span>',
    videoSources: {
      image: {
        src: 'https://assets.isuzu-tis.com/gear_8_speed_7822aef2c9.jpg',
      },
      video: {
        src: 'https://assets.isuzu-tis.com/isuzu_ddi_maxforce_gear8speed_ad113d3484.mp4',
      },
    },
  },
  {
    hotspotId: 'injection',
    title: 'หัวฉีดแรงดันสูง<br>Gi-BOOST',
    description:
      'เทคโนโลยี Gi-BOOST (Genius Injector Boosted Output & Optimized Spray Technology) <span>แรงดันสูง 250 Mpa</span> <span>ให้การจุดระเบิดทุกครั้ง</span> <span>เต็มประสิทธิภาพการเผาไหม้</span>',
    videoSources: {
      image: {
        src: 'https://assets.isuzu-tis.com/gi_boost_483edbb38a.jpg',
      },
      video: {
        src: 'https://assets.isuzu-tis.com/isuzu_ddi_maxforce_giboost_d526611464.mp4',
      },
    },
  },
];

export const ENGINE_STORY_SECTION_DATA = {
  titleLead: 'THE',
  title: {
    prefix: 'ABSOLUTE',
    suffix: 'POWER',
  },
  description: 'พลังใหม่! <span>ไบเทอร์โบ</span> แรง...<span>หยุดโลก</span>',
  videoSources: STORY_VIDEO_SOURCES,
  connectedSceneVideoSources: STORY_CONNECTED_SCENE_VIDEO_SOURCES,
  hotspotItems: HOTSPOT_ITEMS,
};
